import type { ReactNode } from 'react';
import { memo, useContext, useMemo } from 'react';
import type { To } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import type { RolesInput } from '@/js/libs/use-auth';
import { AuthContext, isEmptyPermissions, matchRoles, useIdentityUser } from '@/js/libs/use-auth';

export interface Condition {
  roles: RolesInput;
  to: To;
}

interface RoleBasedRedirectorProps {
  conditions: Condition[];
  noAccessElement: ReactNode;
  emptyPermissionsRedirect?: To;
}

const RoleBasedRedirector = ({ conditions, noAccessElement, emptyPermissionsRedirect }: RoleBasedRedirectorProps) => {
  const { identityRoles } = useContext(AuthContext);
  const user = useIdentityUser();

  const matchingCondition = useMemo<Condition | undefined>(
    () => conditions.find((condition) => matchRoles(condition.roles, identityRoles)),
    [conditions, identityRoles],
  );

  if (!matchingCondition) {
    if (emptyPermissionsRedirect && isEmptyPermissions(user?.permissions)) {
      return <Navigate to={emptyPermissionsRedirect} replace />;
    }

    return <>{noAccessElement}</>;
  }

  return <Navigate to={matchingCondition.to} replace />;
};

export default memo(RoleBasedRedirector);
